import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router";
import { useDataProvider } from "ra-core";
import { Search, Briefcase, Users, FileText, Receipt, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useDebouncedCallback } from "use-debounce";

interface SearchResult {
  id: string | number;
  type: "job" | "contact" | "proposal" | "invoice";
  title: string;
  subtitle?: string;
}

const typeConfig = {
  job: {
    icon: Briefcase,
    label: "Jobs",
    color: "text-blue-600 dark:text-blue-400",
    path: "/jobs",
  },
  contact: {
    icon: Users,
    label: "Contacts",
    color: "text-green-600 dark:text-green-400",
    path: "/contacts",
  },
  proposal: {
    icon: FileText,
    label: "Proposals",
    color: "text-purple-600 dark:text-purple-400",
    path: "/proposals",
  },
  invoice: {
    icon: Receipt,
    label: "Invoices",
    color: "text-orange-600 dark:text-orange-400",
    path: "/invoices",
  },
};

export const GlobalSearch = () => {
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Helper to check if a string matches the search query
  const matchesQuery = (value: string | undefined | null, query: string) => {
    if (!value) return false;
    return value.toLowerCase().includes(query.toLowerCase());
  };

  // Search function
  const performSearch = useCallback(
    async (searchQuery: string) => {
      if (!searchQuery.trim()) {
        setResults([]);
        return;
      }

      setIsLoading(true);
      const query = searchQuery.toLowerCase().trim();

      try {
        const searchResults: SearchResult[] = [];

        // Fetch contacts first (we'll use them to match contact names with jobs)
        const { data: contacts } = await dataProvider.getList("contacts", {
          pagination: { page: 1, perPage: 100 },
          sort: { field: "created_at", order: "DESC" },
          filter: {},
        });

        // Create a map of contact IDs to names
        const contactMap = new Map<number, string>();
        contacts?.forEach((contact: any) => {
          contactMap.set(contact.id, contact.name);
        });

        // Search contacts by name, email, phone, or address
        const matchingContacts = contacts?.filter(
          (contact: any) =>
            matchesQuery(contact.name, query) ||
            matchesQuery(contact.email, query) ||
            matchesQuery(contact.phone, query) ||
            matchesQuery(contact.address, query),
        );

        matchingContacts?.slice(0, 5).forEach((contact: any) => {
          searchResults.push({
            id: contact.id,
            type: "contact",
            title: contact.name,
            subtitle: contact.email || contact.phone,
          });
        });

        // Search jobs by address or contact name
        const { data: jobs } = await dataProvider.getList("jobs", {
          pagination: { page: 1, perPage: 100 },
          sort: { field: "updated_at", order: "DESC" },
          filter: {},
        });

        const matchingJobs = jobs?.filter((job: any) => {
          const contactName = contactMap.get(job.contact_id) || "";
          return (
            matchesQuery(job.address, query) ||
            matchesQuery(contactName, query) ||
            matchesQuery(job.job_type, query)
          );
        });

        matchingJobs?.slice(0, 5).forEach((job: any) => {
          const contactName = contactMap.get(job.contact_id);
          const jobType = job.job_type?.replace(/_/g, " ");
          const subtitleParts = [jobType, contactName].filter(Boolean);
          searchResults.push({
            id: job.id,
            type: "job",
            title: job.address || `Job #${job.id}`,
            subtitle: subtitleParts.join(" • "),
          });
        });

        // Create job info map (address and contact_id)
        const jobInfoMap = new Map<
          number,
          { address: string; contactId: number }
        >();
        jobs?.forEach((job: any) => {
          jobInfoMap.set(job.id, {
            address: job.address,
            contactId: job.contact_id,
          });
        });

        // Search proposals by job address or customer name
        const { data: proposals } = await dataProvider.getList("proposals", {
          pagination: { page: 1, perPage: 50 },
          sort: { field: "created_at", order: "DESC" },
          filter: {},
        });

        const matchingProposals = proposals?.filter((p: any) => {
          const jobInfo = jobInfoMap.get(p.job_id);
          const jobAddress = jobInfo?.address || "";
          const customerName = jobInfo
            ? contactMap.get(jobInfo.contactId) || ""
            : "";
          return (
            matchesQuery(jobAddress, query) ||
            matchesQuery(customerName, query) ||
            matchesQuery(p.status, query) ||
            String(p.id).includes(query)
          );
        });

        matchingProposals?.slice(0, 3).forEach((proposal: any) => {
          const jobInfo = jobInfoMap.get(proposal.job_id);
          const customerName = jobInfo
            ? contactMap.get(jobInfo.contactId)
            : null;
          const subtitleParts = [
            jobInfo?.address,
            customerName,
            `$${proposal.total?.toLocaleString()}`,
          ].filter(Boolean);
          searchResults.push({
            id: proposal.id,
            type: "proposal",
            title: `Proposal #${proposal.id}`,
            subtitle: subtitleParts.join(" • "),
          });
        });

        // Search invoices by job address or customer name
        const { data: invoices } = await dataProvider.getList("invoices", {
          pagination: { page: 1, perPage: 50 },
          sort: { field: "created_at", order: "DESC" },
          filter: {},
        });

        const matchingInvoices = invoices?.filter((i: any) => {
          const jobInfo = jobInfoMap.get(i.job_id);
          const jobAddress = jobInfo?.address || "";
          const customerName = jobInfo
            ? contactMap.get(jobInfo.contactId) || ""
            : "";
          return (
            matchesQuery(jobAddress, query) ||
            matchesQuery(customerName, query) ||
            matchesQuery(i.status, query) ||
            String(i.id).includes(query)
          );
        });

        matchingInvoices?.slice(0, 3).forEach((invoice: any) => {
          const jobInfo = jobInfoMap.get(invoice.job_id);
          const customerName = jobInfo
            ? contactMap.get(jobInfo.contactId)
            : null;
          const subtitleParts = [
            jobInfo?.address,
            customerName,
            `$${invoice.amount?.toLocaleString()}`,
          ].filter(Boolean);
          searchResults.push({
            id: invoice.id,
            type: "invoice",
            title: `Invoice #${invoice.id}`,
            subtitle: subtitleParts.join(" • "),
          });
        });

        setResults(searchResults);
        setSelectedIndex(0);
      } catch (error) {
        console.error("Search error:", error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    },
    [dataProvider],
  );

  const debouncedSearch = useDebouncedCallback(performSearch, 300);

  useEffect(() => {
    if (query) {
      debouncedSearch(query);
    } else {
      setResults([]);
    }
  }, [query, debouncedSearch]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && results[selectedIndex]) {
      e.preventDefault();
      handleSelect(results[selectedIndex]);
    } else if (e.key === "Escape") {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  const handleSelect = (result: SearchResult) => {
    const config = typeConfig[result.type];
    navigate(`${config.path}/${result.id}/show`);
    setQuery("");
    setIsOpen(false);
  };

  const clearSearch = () => {
    setQuery("");
    setResults([]);
    inputRef.current?.focus();
  };

  // Group results by type
  const groupedResults = results.reduce(
    (acc, result) => {
      if (!acc[result.type]) {
        acc[result.type] = [];
      }
      acc[result.type].push(result);
      return acc;
    },
    {} as Record<string, SearchResult[]>,
  );

  return (
    <div ref={containerRef} className="relative flex-1 max-w-md">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search jobs, contacts, proposals..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          className="pl-9 pr-8 h-9 rounded"
        />
        {query && (
          <button
            onClick={clearSearch}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Results dropdown */}
      {isOpen && (query || results.length > 0) && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-background border rounded-md shadow-lg z-50 max-h-[400px] overflow-y-auto">
          {isLoading && (
            <div className="px-4 py-3 text-sm text-muted-foreground">
              Searching...
            </div>
          )}

          {!isLoading && query && results.length === 0 && (
            <div className="px-4 py-3 text-sm text-muted-foreground">
              No results found for "{query}"
            </div>
          )}

          {!isLoading &&
            Object.entries(groupedResults).map(([type, typeResults]) => {
              const config = typeConfig[type as keyof typeof typeConfig];
              const Icon = config.icon;

              return (
                <div key={type}>
                  <div className="px-3 py-2 text-xs font-medium text-muted-foreground bg-muted/50 flex items-center gap-2">
                    <Icon className={cn("h-3.5 w-3.5", config.color)} />
                    {config.label}
                  </div>
                  {typeResults.map((result) => {
                    const globalIndex = results.indexOf(result);
                    return (
                      <button
                        key={`${result.type}-${result.id}`}
                        onClick={() => handleSelect(result)}
                        className={cn(
                          "w-full px-4 py-2 text-left flex flex-col hover:bg-muted/50 transition-colors",
                          globalIndex === selectedIndex && "bg-muted",
                        )}
                      >
                        <span className="text-sm font-medium truncate">
                          {result.title}
                        </span>
                        {result.subtitle && (
                          <span className="text-xs text-muted-foreground truncate">
                            {result.subtitle}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};
